/**
 * Active trade account + broker (multi-broker): GET /broker/trade-accounts,
 * PUT /broker/active-trade-account. Replaces Liquid-only credential/account loading.
 */

import { toastError, toastWarning, presentMutationError } from "../utils/showApiErrorToast";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import type { BrokerAccount } from "../services/liquidBrokersApi";
import {
  fetchTradeAccounts,
  putActiveTradeAccount,
  type TradeAccountAnalytics,
  type TradeAccountApiRow,
} from "../services/brokerIntegrationApi";
import { invalidateTradeDataCaches, notifyTradesUpdated } from "../hooks/useTradeStatistics";
import { writeDashboardDateWindow } from "../utils/dashboardDateWindowStorage";
import { startOfIanaCalendarDayUtc, endOfIanaCalendarDayUtc } from "../utils/notificationPeriod";
import {
  ctraderDisplayFromTradeAccountRow,
  formatCtraderTradeAccountLabel,
} from "../utils/ctraderAccountDisplay";
import { CTRADER_BROKER_SLUG } from "../utils/ctraderBroker";
import {
  isCustomCsvBrokerName,
  resolveCustomCsvTradeAccountLabels,
} from "../utils/customCsvAccountDisplay";

/** @deprecated Legacy Liquid-only storage; migrated away when trade-accounts loads */
export const SYNCED_ACCOUNT_CODE_KEY = "liquidbrokers_synced_account_code";

const ACTIVE_TRADE_ACCOUNT_CACHE_KEY = "tessera_active_trade_account_v1";

export type TradeAccountOption = BrokerAccount & {
  brokerName: string;
  brokerDisplayName: string;
  tradeLabel: string;
  /** From GET /broker/trade-accounts `source` (credentials | broker_list | synced_trades | imported_trades) */
  accountSource?: string;
  /** ISO from `last_broker_sync_at` when credential-backed */
  lastBrokerSyncAt?: string | null;
  /** ISO from `last_csv_import_at` when CSV-backed trades exist */
  lastCsvImportAt?: string | null;
  /** Server-resolved analytics for this account (dashboard / statistics UI). */
  analytics?: TradeAccountAnalytics;
  /** From GET /broker/trade-accounts — unresolved instrument market-data setup. */
  instrumentSetupPending?: boolean | null;
  /** cTrader: the account number shown in cTrader (traderLogin); accountCode is its API ctid. */
  platformLogin?: string | null;
};

function mapTradeAccountRow(row: TradeAccountApiRow): TradeAccountOption {
  const rawBn = String(row.broker_name || "").toLowerCase();
  const bn = rawBn === "liquid" ? "liquidbrokers" : rawBn;
  const custom = row.custom_name ?? row.customName ?? null;
  const demoRaw = row.is_demo ?? row.isDemo;
  const lastBroker = row.last_broker_sync_at ?? row.lastBrokerSyncAt ?? null;
  const lastCsv = row.last_csv_import_at ?? row.lastCsvImportAt ?? null;
  const trimOrNull = (v: unknown): string | null => {
    if (v == null) return null;
    const t = String(v).trim();
    return t || null;
  };
  const trimmedLabel = (row.label || "").trim();
  let brokerDisplayName = row.display_name || bn;
  let tradeLabel = trimmedLabel || `${row.display_name || bn} – ${row.account_code}`;
  let platformLogin: string | null = null;
  if (bn === CTRADER_BROKER_SLUG) {
    const ctrader = ctraderDisplayFromTradeAccountRow(row);
    if (ctrader) {
      platformLogin = ctrader.platformLogin;
      // Keep the platform/integration broker name as cTrader.
      // Executing broker (FxPro/icmarketseu/etc.) belongs in the account label only.
      if (!trimmedLabel) {
        tradeLabel = formatCtraderTradeAccountLabel(ctrader);
      }
    }
  } else if (isCustomCsvBrokerName(bn)) {
    const customCsv = resolveCustomCsvTradeAccountLabels(row);
    brokerDisplayName = customCsv.brokerDisplayName;
    tradeLabel = customCsv.tradeLabel;
  }
  return {
    accountCode: row.account_code,
    brokerName: bn,
    brokerDisplayName,
    tradeLabel,
    accountSource: row.source,
    accountStatus: "",
    baseCurrency: "",
    customName: custom,
    isDemo: demoRaw == null ? undefined : Boolean(demoRaw),
    lastBrokerSyncAt: trimOrNull(lastBroker),
    lastCsvImportAt: trimOrNull(lastCsv),
    platformLogin,
    analytics: row.analytics,
    instrumentSetupPending:
      row.instrument_setup_pending ?? row.instrumentSetupPending ?? null,
  };
}

export interface UseActiveBrokerAccountResult {
  /** Logical Tessera account code for the active broker context */
  activeAccountCode: string | null;
  /** Lower-case broker slug (liquidbrokers, oanda, …) */
  activeBrokerName: string | null;
  accounts: TradeAccountOption[];
  hasMultipleCtraderAccounts: boolean;
  ctraderFoundAccounts: TradeAccountOption[];
  loading: boolean;
  error: string | null;
  /** True when user has at least one credential-backed trade account */
  isConnected: boolean;
  setActiveTradeAccount: (brokerName: string, accountCode: string) => Promise<void>;
  /**
   * Reload trade accounts; returns active account code after load.
   * Defaults to silent (does not flip the global loading gate) so sync/import
   * success paths do not remount Broker Management / Dashboard full-page loaders.
   */
  refetch: (options?: { silent?: boolean }) => Promise<string | null>;
  /**
   * After Tessera data removal: switch active account if needed (picker list comes from the next refetch).
   */
  removeTradeAccount: (brokerName: string, accountCode: string) => Promise<void>;
}

function readCachedActivePair(): { broker_name: string; account_code: string } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ACTIVE_TRADE_ACCOUNT_CACHE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    const bn = typeof o?.broker_name === "string" ? o.broker_name.trim().toLowerCase() : "";
    const ac = typeof o?.account_code === "string" ? o.account_code.trim() : "";
    if (!bn || !ac) return null;
    return { broker_name: bn, account_code: ac };
  } catch {
    return null;
  }
}

function writeCachedActivePair(brokerName: string, accountCode: string): void {
  try {
    window.localStorage.setItem(
      ACTIVE_TRADE_ACCOUNT_CACHE_KEY,
      JSON.stringify({
        broker_name: brokerName.toLowerCase(),
        account_code: accountCode,
      })
    );
  } catch {
    // ignore
  }
}

const ActiveBrokerAccountContext = createContext<UseActiveBrokerAccountResult | null>(null);

export function ActiveBrokerAccountProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [activeAccountCode, setActiveAccountCodeState] = useState<string | null>(null);
  const [activeBrokerName, setActiveBrokerNameState] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<TradeAccountOption[]>([]);
  const [hasMultipleCtraderAccounts, setHasMultipleCtraderAccounts] = useState(false);
  const [ctraderFoundAccounts, setCtraderFoundAccounts] = useState<TradeAccountOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const lastAppliedDeepLinkRef = useRef<string | null>(null);
  const lastAppliedWindowRef = useRef<string | null>(null);

  const applyActiveFromPayload = useCallback(
    (
      active: { broker_name: string; account_code: string } | null,
      list: TradeAccountOption[]
    ) => {
      if (active?.broker_name && active?.account_code) {
        const bn = active.broker_name.toLowerCase();
        const ac = active.account_code;
        setActiveBrokerNameState(bn);
        setActiveAccountCodeState(ac);
        writeCachedActivePair(bn, ac);
        return ac;
      }
      const cached = readCachedActivePair();
      if (cached && list.some((a) => a.brokerName === cached.broker_name && a.accountCode === cached.account_code)) {
        setActiveBrokerNameState(cached.broker_name);
        setActiveAccountCodeState(cached.account_code);
        return cached.account_code;
      }
      if (list.length > 0) {
        const first = list[0];
        setActiveBrokerNameState(first.brokerName);
        setActiveAccountCodeState(first.accountCode);
        writeCachedActivePair(first.brokerName, first.accountCode);
        return first.accountCode;
      }
      setActiveBrokerNameState(null);
      setActiveAccountCodeState(null);
      return null;
    },
    []
  );

  const load = useCallback(async (options?: { silent?: boolean }): Promise<string | null> => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await fetchTradeAccounts();
      const list = (data.accounts || []).map(mapTradeAccountRow);
      const ctraderList = (data.ctrader_found_accounts || []).map(mapTradeAccountRow);
      setAccounts(list);
      setCtraderFoundAccounts(ctraderList);
      setHasMultipleCtraderAccounts(
        Boolean(data.has_multiple_ctrader_accounts) && ctraderList.length > 1
      );
      setIsConnected(list.length > 0);
      const effective = applyActiveFromPayload(data.active, list);
      return effective;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load trade accounts";
      setError(msg);
      setAccounts([]);
      setCtraderFoundAccounts([]);
      setHasMultipleCtraderAccounts(false);
      setIsConnected(false);
      const fallback = readCachedActivePair();
      if (fallback) {
        setActiveBrokerNameState(fallback.broker_name);
        setActiveAccountCodeState(fallback.account_code);
        return fallback.account_code;
      }
      setActiveBrokerNameState(null);
      setActiveAccountCodeState(null);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [applyActiveFromPayload]);

  /** Post-sync/import refresh: silent by default so full-page loaders do not flash. */
  const refetch = useCallback(
    (options?: { silent?: boolean }) => load({ silent: options?.silent !== false }),
    [load]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const setActiveTradeAccount = useCallback(
    async (brokerName: string, accountCode: string) => {
      const bn = brokerName.toLowerCase();
      const prevBroker = activeBrokerName;
      const prevAccount = activeAccountCode;

      // Optimistic update: reflect account switch in the UI immediately.
      writeCachedActivePair(bn, accountCode);
      setActiveBrokerNameState(bn);
      setActiveAccountCodeState(accountCode);
      invalidateTradeDataCaches();
      window.dispatchEvent(
        new CustomEvent("tradesUpdated", {
          detail: { action: "accountSwitch", accountCode, brokerName: bn },
        })
      );

      try {
        await putActiveTradeAccount(bn, accountCode);
      } catch (error) {
        // Roll back local state/cache if the server rejects the update.
        if (prevBroker && prevAccount) {
          writeCachedActivePair(prevBroker, prevAccount);
          setActiveBrokerNameState(prevBroker);
          setActiveAccountCodeState(prevAccount);
        } else {
          try {
            window.localStorage.removeItem(ACTIVE_TRADE_ACCOUNT_CACHE_KEY);
          } catch {
            // ignore
          }
          setActiveBrokerNameState(prevBroker ?? null);
          setActiveAccountCodeState(prevAccount ?? null);
        }
        throw error;
      }
    },
    [activeBrokerName, activeAccountCode]
  );

  const removeTradeAccount = useCallback(
    async (brokerName: string, accountCode: string) => {
      const bn = brokerName.toLowerCase() === "liquid" ? "liquidbrokers" : brokerName.toLowerCase();
      const wasActive = activeBrokerName === bn && activeAccountCode === accountCode;
      if (!wasActive) return;

      const remaining = accounts.filter(
        (a) => !(a.brokerName === bn && a.accountCode === accountCode)
      );
      if (remaining.length > 0) {
        const first = remaining[0];
        await putActiveTradeAccount(first.brokerName, first.accountCode);
        writeCachedActivePair(first.brokerName, first.accountCode);
        setActiveBrokerNameState(first.brokerName);
        setActiveAccountCodeState(first.accountCode);
      } else {
        setActiveBrokerNameState(null);
        setActiveAccountCodeState(null);
        try {
          window.localStorage.removeItem(ACTIVE_TRADE_ACCOUNT_CACHE_KEY);
        } catch {
          // ignore
        }
      }
      invalidateTradeDataCaches();
    },
    [accounts, activeBrokerName, activeAccountCode]
  );

  // Deep link: broker_account_code / account_code — optional broker_name for multi-broker
  useEffect(() => {
    if (loading) return;
    const q = new URLSearchParams(location.search);
    const desiredAccount = (q.get("broker_account_code") || q.get("account_code") || "").trim();
    if (!desiredAccount) return;

    const brokerParam = (q.get("broker_name") || "").trim().toLowerCase();
    const deepKey = `${brokerParam}|${desiredAccount}`;
    if (lastAppliedDeepLinkRef.current === deepKey) return;

    if (
      activeBrokerName &&
      activeAccountCode &&
      desiredAccount === activeAccountCode &&
      (!brokerParam || brokerParam === activeBrokerName)
    ) {
      lastAppliedDeepLinkRef.current = deepKey;
      return;
    }

    let targetBroker = brokerParam;
    if (!targetBroker && accounts.length > 0) {
      const matches = accounts.filter((a) => a.accountCode === desiredAccount);
      if (matches.length === 1) {
        targetBroker = matches[0].brokerName;
      } else if (matches.length > 1) {
        toastWarning("Ambiguous account code", {
          description: "Add broker_name to the URL when multiple brokers use the same account code.",
        });
        lastAppliedDeepLinkRef.current = deepKey;
        return;
      }
    }
    if (!targetBroker && accounts.length > 0) {
      targetBroker = accounts[0].brokerName;
    }
    if (!targetBroker) return;

    lastAppliedDeepLinkRef.current = deepKey;

    void (async () => {
      try {
        await setActiveTradeAccount(targetBroker, desiredAccount);
        toast.info("Viewing account", {
          description: `Now viewing ${desiredAccount}`,
        });
      } catch (e) {
        lastAppliedDeepLinkRef.current = null;
        presentMutationError(e, "Could not switch account");
      }
    })();
  }, [
    location.search,
    loading,
    activeAccountCode,
    activeBrokerName,
    accounts,
    setActiveTradeAccount,
  ]);

  useEffect(() => {
    if (loading) return;
    const q = new URLSearchParams(location.search);
    const localStart = (q.get("local_start_date") || "").trim();
    const localEnd = (q.get("local_end_date") || "").trim();
    const tz = (q.get("timezone") || "").trim();
    if (!localStart || !localEnd || !tz) return;

    const key = `${localStart}|${localEnd}|${tz}`;
    if (lastAppliedWindowRef.current === key) return;
    lastAppliedWindowRef.current = key;

    try {
      const startIso = startOfIanaCalendarDayUtc(localStart as any, tz).toISOString();
      const endIso = endOfIanaCalendarDayUtc(localEnd as any, tz).toISOString();
      writeDashboardDateWindow({
        v: 1,
        mode: "custom",
        timeframe: "today",
        startDate: startIso,
        endDate: endIso,
      });
    } catch {
      // no-op
    }
  }, [location.search, loading]);

  const value = useMemo<UseActiveBrokerAccountResult>(
    () => ({
      activeAccountCode,
      activeBrokerName,
      accounts,
      hasMultipleCtraderAccounts,
      ctraderFoundAccounts,
      loading,
      error,
      isConnected,
      setActiveTradeAccount,
      refetch,
      removeTradeAccount,
    }),
    [
      activeAccountCode,
      activeBrokerName,
      accounts,
      hasMultipleCtraderAccounts,
      ctraderFoundAccounts,
      loading,
      error,
      isConnected,
      setActiveTradeAccount,
      refetch,
      removeTradeAccount,
    ]
  );

  return (
    <ActiveBrokerAccountContext.Provider value={value}>{children}</ActiveBrokerAccountContext.Provider>
  );
}

export function useActiveBrokerAccount(): UseActiveBrokerAccountResult {
  const ctx = useContext(ActiveBrokerAccountContext);
  if (!ctx) {
    throw new Error("useActiveBrokerAccount must be used within ActiveBrokerAccountProvider");
  }
  return ctx;
}

/** Safe outside Admin/broker providers (e.g. public demo). */
export function useActiveBrokerAccountOptional(): UseActiveBrokerAccountResult | null {
  return useContext(ActiveBrokerAccountContext);
}
